import React from 'react';
import './ControlPanel.css';

const STONE_SHAPES = [
  { code: 'RO', label: 'Round', img: 'round' },
  { code: 'EM', label: 'Emerald', img: 'emerald' },
  { code: 'OV', label: 'Oval', img: 'oval' },
  { code: 'MR', label: 'Marquise', img: 'marquise' },
  { code: 'PR', label: 'Princess', img: 'princess' },
  { code: 'PE', label: 'Pear', img: 'pear' },
];

const METAL_COLORS = {
  YellowGold: { swatch: '#f5c069', label: 'Yellow' },
  WhiteGold: { swatch: '#dfdfdf', label: 'White' },
  RoseGold: { swatch: '#e4aa80', label: 'Rose' },
};

const METAL_TYPES = [
  { value: 'gold18k', label: '18K Gold' },
  { value: 'gold14k', label: '14K Gold' },
  { value: 'gold8k', label: '8K Gold' },
  { value: 'silver', label: 'Silver' },
];

const SETTING_TYPES_ROUND_OVAL = [
  { data: 'AH-NO', label: 'AH-NO', img: 'https://cdn.siriuspirlanta.com/byd/ah-no.png' },
  { data: 'AY-NO', label: 'AY-NO', img: 'https://cdn.siriuspirlanta.com/byd/ay-no.png' },
  { data: 'AY-ST', label: 'AY-ST', img: 'https://cdn.siriuspirlanta.com/byd/ay-st.png' },
  { data: 'AY-HL', label: 'AY-HL', img: 'https://cdn.siriuspirlanta.com/byd/ay-hl.png' },
  { data: 'AK-NO', label: 'AK-NO', img: 'https://cdn.siriuspirlanta.com/byd/ak-no.png' },
  { data: 'AK-ST', label: 'AK-ST', img: 'https://cdn.siriuspirlanta.com/byd/ak-st.png' },
];

const SETTING_TYPES_OTHER = [
  { data: 'SY-NO', label: 'SY-NO', img: 'https://cdn.siriuspirlanta.com/byd/sy-no.png' },
  { data: 'SY-ST', label: 'SY-ST', img: 'https://cdn.siriuspirlanta.com/byd/sy-st.png' },
  { data: 'SY-HL', label: 'SY-HL', img: 'https://cdn.siriuspirlanta.com/byd/sy-hl.png' },
  { data: 'SK-NO', label: 'SK-NO', img: 'https://cdn.siriuspirlanta.com/byd/sk-no.png' },
  { data: 'SK-ST', label: 'SK-ST', img: 'https://cdn.siriuspirlanta.com/byd/sk-st.png' },
  { data: 'SH-NO', label: 'SH-NO', img: 'https://cdn.siriuspirlanta.com/byd/sh-no.png' },
];

function ControlPanel({
  stoneType, setStoneType,
  stoneSize, setStoneSize, stoneSizeOptions,
  stoneOrigin, setStoneOrigin,
  yuvaType, setYuvaType,
  metalColor, setMetalColor,
  metalType, setMetalType,
  bandType, setBandType,
  ringSize, setRingSize,
  onBuy,
  cartStatus,
  cartError,
  orderSummary,
  currentPrice,
  isFullyConfigured,
  getPriceDiff,
  formatPrice,
}) {
  // Fiyat farkı etiketi: sadece tamamen konfigüre edilmiş durumda rakamsal fark gösterir
  const PriceDiffTag = ({ category, value, isActive }) => {
    if (isActive) return <span className="price-tag price-tag-active">✓</span>;
    if (!isFullyConfigured) return null;
    const diff = getPriceDiff(category, value);
    if (diff === 0) return null;
    const formatted = formatPrice(Math.abs(diff));
    return (
      <span className={`price-tag ${diff > 0 ? 'price-tag-up' : 'price-tag-down'}`}>
        {diff > 0 ? '+' : '-'}{formatted}
      </span>
    );
  };

  // Adım numaralarını yeni sıraya göre üretiyoruz.
  // Her adımın erişilebilir olması için önceki adımın yapılmış olması şart değil,
  // sadece görsel olarak "aktif/bekliyor/tamam" durumunu vurguluyoruz.
  const steps = {
    band: { num: 1, done: !!bandType },
    metalColor: { num: 2, done: !!metalColor },
    metalType: { num: 3, done: !!metalType },
    stoneShape: { num: 4, done: !!stoneType },
    caratSize: { num: 5, done: !!(stoneType && stoneSize) },
    setting: { num: 6, done: !!(stoneType && stoneSize && yuvaType) },
  };

  const StepHeader = ({ step, children }) => (
    <h3>
      <span className={`step-number ${step.done ? 'step-done' : ''}`}>
        {step.done ? '✓' : step.num}
      </span>
      {children}
    </h3>
  );

  // Setting type modelinin seçimi stoneType'a bağlı
  const isRoundOrOval = stoneType === 'RO' || stoneType === 'OV';
  const settingTypes = isRoundOrOval ? SETTING_TYPES_ROUND_OVAL : SETTING_TYPES_OTHER;
  const canPickSetting = !!(stoneType && stoneSize);
  const canPickCarat = !!stoneType;

  return (
    <div id="control-panel">
      <div className="panel-header">
        <h2 className="panel-title">Build Your Ring</h2>
        <p className="panel-subtitle">Customize every detail</p>
      </div>

      {/* 1. Band Style */}
      <div className="control-section">
        <StepHeader step={steps.band}>Band Style</StepHeader>
        <div className="button-grid band-type-grid">
          {[...Array(4)].map((_, i) => (
            <button
              key={i + 1}
              className={`kolar-btn ${bandType === String(i + 1) ? 'active' : ''}`}
              onClick={() => setBandType(String(i + 1))}
              data-band={String(i + 1)}
            >
              Band {i + 1}
              <PriceDiffTag category="bandType" value={String(i + 1)} isActive={bandType === String(i + 1)} />
            </button>
          ))}
        </div>
      </div>

      {/* 2. Metal Color */}
      <div className="control-section">
        <StepHeader step={steps.metalColor}>Metal Color</StepHeader>
        <div className="button-grid metal-color-grid">
          {Object.entries(METAL_COLORS).map(([key, info]) => (
            <button
              key={key}
              className={`metal-color-btn ${metalColor === key ? 'active' : ''}`}
              onClick={() => setMetalColor(key)}
              title={key}
              data-metal={key}
            >
              <span className="metal-swatch" style={{ backgroundColor: info.swatch }}></span>
              <span className="metal-name">{info.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 3. Metal Type */}
      <div className="control-section">
        <StepHeader step={steps.metalType}>Metal Type</StepHeader>
        <div className="button-grid metal-type-grid">
          {METAL_TYPES.map((type) => (
            <button
              key={type.value}
              className={`metal-type-btn ${metalType === type.value ? 'active' : ''}`}
              onClick={() => setMetalType(type.value)}
              data-metal-type={type.value}
            >
              <span>{type.label}</span>
              <PriceDiffTag category="metalType" value={type.value} isActive={metalType === type.value} />
            </button>
          ))}
        </div>
      </div>

      {/* 4. Stone Shape */}
      <div className="control-section">
        <StepHeader step={steps.stoneShape}>Stone Shape</StepHeader>
        <div className="button-grid stone-grid">
          {STONE_SHAPES.map(({ code, label, img }) => (
            <button
              key={code}
              className={`stone-btn ${stoneType === code ? 'active' : ''}`}
              onClick={() => setStoneType(code)}
              data-stone={code}
            >
              <img
                src={`https://cdn.siriuspirlanta.com/byd/shape/${img}.png`}
                alt={label}
                loading="lazy"
              />
              <span>{label}</span>
              <PriceDiffTag category="stoneType" value={code} isActive={stoneType === code} />
            </button>
          ))}
        </div>
      </div>

      {/* 5. Carat Size */}
      <div className={`control-section ${!canPickCarat ? 'section-locked' : ''}`}>
        <StepHeader step={steps.caratSize}>Carat Size</StepHeader>
        {canPickCarat ? (
          <div className="button-grid size-grid">
            {stoneSizeOptions[stoneType]?.map((option) => (
              <button
                key={option.value}
                className={`size-btn ${stoneSize === option.value ? 'active' : ''}`}
                onClick={() => setStoneSize(option.value)}
                data-size={option.value}
                data-size-code={option.sizeCode}
              >
                {option.text}
                <PriceDiffTag category="stoneSize" value={option.value} isActive={stoneSize === option.value} />
              </button>
            ))}
          </div>
        ) : (
          <p className="section-hint">Select a stone shape first</p>
        )}
      </div>

      {/* 6. Setting Type */}
      <div className={`control-section ${!canPickSetting ? 'section-locked' : ''}`}>
        <StepHeader step={steps.setting}>Setting Type</StepHeader>
        {canPickSetting ? (
          <div className="button-grid setting-grid">
            {settingTypes.map((item) => (
              <button
                key={item.data}
                className={`yuva-btn ${yuvaType === item.data ? 'active' : ''}`}
                onClick={() => setYuvaType(item.data)}
                data-yuva={item.data}
              >
                <img src={item.img} alt={item.label} loading="lazy" />
                <span>{item.label}</span>
                <PriceDiffTag category="yuvaType" value={item.data} isActive={yuvaType === item.data} />
              </button>
            ))}
          </div>
        ) : (
          <p className="section-hint">Pick a stone shape and carat to see settings</p>
        )}
      </div>

      {/* Stone Origin */}
      <div className="control-section">
        <h3><span className="step-number step-muted">•</span>Stone Origin</h3>
        <div className="button-grid origin-grid">
          {[
            { value: 'natural', label: 'Natural', icon: '💎' },
            { value: 'lab', label: 'Lab Grown', icon: '🔬' },
          ].map((origin) => (
            <button
              key={origin.value}
              className={`origin-btn ${stoneOrigin === origin.value ? 'active' : ''}`}
              onClick={() => setStoneOrigin(origin.value)}
              data-origin={origin.value}
            >
              <span className="origin-icon">{origin.icon}</span>
              <span>{origin.label}</span>
              <PriceDiffTag category="stoneOrigin" value={origin.value} isActive={stoneOrigin === origin.value} />
            </button>
          ))}
        </div>
      </div>

      {/* Ring Size */}
      <div className="control-section">
        <h3><span className="step-number step-muted">•</span>Ring Size</h3>
        <div className="ring-size-container">
          <select
            className="ring-size-select"
            value={ringSize}
            onChange={(e) => setRingSize(parseInt(e.target.value))}
          >
            {[...Array(10)].map((_, i) => (
              <option key={9 + i} value={9 + i}>
                {9 + i}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Order Summary */}
      <div className="control-section order-summary-section">
        <h3>Your Selection</h3>
        <div className="order-summary">
          {orderSummary && Object.entries(orderSummary)
            .filter(([key]) => key !== 'Price')
            .map(([key, value]) => (
              <div className="summary-row" key={key}>
                <span className="summary-label">{key}</span>
                <span className={`summary-value ${value === '—' ? 'summary-value-empty' : ''}`}>
                  {value}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Price */}
      <div className="control-section price-section">
        <div className="price-display">
          <span className="price-label">Total Price</span>
          <span className="price-amount">{currentPrice}</span>
        </div>
        {!isFullyConfigured && (
          <p className="price-hint">Complete steps 1–6 to see the final price</p>
        )}
      </div>

      {/* Add to Cart */}
      <div className="control-section cart-section">
        <button
          id="buy-btn"
          className={`add-to-cart-btn ${cartStatus}`}
          onClick={onBuy}
          disabled={
            cartStatus === 'loading' ||
            cartStatus === 'success' ||
            !isFullyConfigured
          }
        >
          {cartStatus === 'loading' && (
            <>
              <span className="spinner"></span>
              Adding...
            </>
          )}
          {cartStatus === 'success' && (
            <>
              <span className="checkmark">✓</span>
              Added to Cart!
            </>
          )}
          {cartStatus === 'error' && 'Try Again'}
          {cartStatus === 'idle' && (
            <>
              <svg className="cart-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {isFullyConfigured ? 'Add to Cart' : 'Complete Your Ring'}
            </>
          )}
        </button>
        {cartStatus === 'error' && cartError && (
          <p className="cart-error-msg">{cartError}</p>
        )}
      </div>
    </div>
  );
}

export default ControlPanel;
